import React, { useState, useEffect } from "react";
import CommonModal from "../../../../components/modals/commonModel";
import DefaultButton from "../../../../components/buttons/defaultButton";
import DefaultBadge from "../../../../components/badge/defaultBadge";
import { HiChevronDown, HiChevronUp } from "react-icons/hi";
import { FaArrowUp, FaArrowDown } from "react-icons/fa";
import moment from "moment";
import { getSalaryAuditLogs } from "../../../../../services/workspace/auditLog.service";
import toast from "react-hot-toast";

const AuditLogViewer = ({ employeeId, onClose }) => {
  const [expandedLogs, setExpandedLogs] = useState({});
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(false);

  const formatDateTime = (dateString) => {
    return moment(dateString).format("DD MMM YYYY, hh:mm A");
  };

  const formatCurrency = (amount) => {
    if (!amount) return "N/A";
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount)) return amount;
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(numAmount);
  };

  const getActionBadge = (action) => {
    switch (action) {
      case "CREATE":
        return <DefaultBadge status={1} custom="Created" />;
      case "UPDATE":
        return <DefaultBadge status={0} custom="Updated" />;
      case "DELETE":
        return <DefaultBadge status={2} custom="Deleted" />;
      default:
        return <DefaultBadge status={0} custom={action} />;
    }
  };

  const toggleExpand = (logId) => {
    setExpandedLogs((prev) => ({
      ...prev,
      [logId]: !prev[logId],
    }));
  };

  // Fetch audit logs from API
  useEffect(() => {
    const fetchAuditLogs = async () => {
      if (!employeeId) return;

      setLoading(true);
      try {
        const response = await getSalaryAuditLogs(employeeId, { per_page: 100 });
        const logsData = response?.data || response || [];

        // Transform API response to match component format
        const transformedLogs = logsData.map((log) => ({
          id: log.id || log.uuid,
          uuid: log.uuid,
          timestamp: log.created_at || log.timestamp,
          user: {
            id: log.user?.uuid || log.user?.id,
            name: log.user?.name || "System",
            email: log.user?.email || "",
          },
          action: log.action || "UPDATE",
          shortDiff: log.description || log.short_diff || `${log.action} salary assignment`,
          fieldDiffs: log.field_diffs || log.fieldDiffs || [],
        }));

        setAuditLogs(transformedLogs);
      } catch (error) {
        console.error("Failed to fetch audit logs:", error);
        toast.error("Failed to fetch audit logs");
        setAuditLogs([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAuditLogs();
  }, [employeeId]);

  const formatFieldName = (fieldName) => {
    return fieldName
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  return (
    <CommonModal
      modalSize="xl"
      headerTitle="Salary Audit Log"
      show={true}
      onClose={onClose}
    >
      <div className="flex flex-col gap-4">
        <div className="bg-information-50 border border-information-200 rounded-lg p-4">
          <p className="text-sm text-information-900">
            This log shows all changes made to salary assignments, including who made the
            change and when.
          </p>
        </div>

        {/* Audit Log List */}
        <div className="space-y-3">
          {loading ? (
            <div className="text-center py-8 text-textLight">
              <p>Loading audit logs...</p>
            </div>
          ) : auditLogs.length === 0 ? (
            <div className="text-center py-8 text-textLight">
              <p>No audit logs found</p>
            </div>
          ) : (
            auditLogs.map((log) => {
            const isExpanded = expandedLogs[log.id];
            return (
              <div
                key={log.id}
                className="bg-white border border-borderColor rounded-lg overflow-hidden"
              >
                {/* Log Header */}
                <div
                  className="p-4 hover:bg-primary-50 cursor-pointer"
                  onClick={() => toggleExpand(log.id)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        {getActionBadge(log.action)}
                        <span className="text-sm font-medium text-theme">
                          {log.user.name}
                        </span>
                        <span className="text-xs text-textLight">{log.user.email}</span>
                      </div>
                      <p className="text-sm text-theme mb-1">{log.shortDiff}</p>
                      <p className="text-xs text-textLight">
                        {formatDateTime(log.timestamp)}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-textLight">
                        {log.fieldDiffs.length} change{log.fieldDiffs.length !== 1 ? "s" : ""}
                      </span>
                      {isExpanded ? (
                        <HiChevronUp className="text-theme" />
                      ) : (
                        <HiChevronDown className="text-theme" />
                      )}
                    </div>
                  </div>
                </div>

                {/* Expanded Details */}
                {isExpanded && (
                  <div className="border-t border-borderColor bg-primary-50 p-4">
                    <h5 className="text-sm font-semibold text-theme mb-3">
                      Field Changes
                    </h5>
                    <div className="space-y-2">
                      {log.fieldDiffs.map((diff, idx) => {
                        const isIncrease =
                          diff.old &&
                          diff.new &&
                          parseFloat(diff.new) > parseFloat(diff.old);
                        const isDecrease =
                          diff.old &&
                          diff.new &&
                          parseFloat(diff.new) < parseFloat(diff.old);
                        const isNumeric =
                          diff.old &&
                          diff.new &&
                          !isNaN(parseFloat(diff.old)) &&
                          !isNaN(parseFloat(diff.new));

                        return (
                          <div
                            key={idx}
                            className="bg-white rounded p-3 border border-borderColor"
                          >
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-medium text-theme">
                                {formatFieldName(diff.field)}
                              </span>
                              {isIncrease && (
                                <span className="text-xs text-success-600 flex items-center gap-1">
                                  <FaArrowUp className="text-xs" /> Increased
                                </span>
                              )}
                              {isDecrease && (
                                <span className="text-xs text-error-600 flex items-center gap-1">
                                  <FaArrowDown className="text-xs" /> Decreased
                                </span>
                              )}
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <p className="text-xs text-textLight mb-1">Previous Value</p>
                                <p
                                  className={`text-sm font-medium ${
                                    isDecrease
                                      ? "text-error-600 line-through"
                                      : "text-textLight"
                                  }`}
                                >
                                  {isNumeric
                                    ? formatCurrency(diff.old)
                                    : diff.old || "N/A"}
                                </p>
                              </div>
                              <div>
                                <p className="text-xs text-textLight mb-1">New Value</p>
                                <p
                                  className={`text-sm font-medium ${
                                    isIncrease
                                      ? "text-success-600"
                                      : isDecrease
                                      ? "text-error-600"
                                      : "text-theme"
                                  }`}
                                >
                                  {isNumeric
                                    ? formatCurrency(diff.new)
                                    : diff.new || "N/A"}
                                </p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
            })
          )}
        </div>
      </div>
    </CommonModal>
  );
};

export default AuditLogViewer;

